import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { HiDotsHorizontal, HiChatAlt } from 'react-icons/hi';
import navItems from '../../utils/navItems';
import { buildFeedbackMailto } from '@coparent/shared/src/utils/feedback';

const feedbackHref = buildFeedbackMailto({
  platform: 'Web',
  version: process.env.REACT_APP_VERSION ?? 'web',
});

const primaryItems = navItems.filter(item => !item.mobileTab);
const moreItems = navItems.filter(item => item.mobileTab === 'more');
const moreRoutes = moreItems.map(item => item.to);

const MobileNav = () => {
  const [moreOpen, setMoreOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  const isMoreActive = moreRoutes.some(route => location.pathname.startsWith(route));

  // Close menu on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Close menu on click outside
  useEffect(() => {
    if (!moreOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [moreOpen]);

  const tabClass = (isActive) =>
    `flex flex-col items-center gap-0.5 flex-1 py-1.5 rounded-xl text-[0.625rem] font-semibold transition-all duration-200 ${
      isActive ? 'text-canopy bg-accent-50' : 'text-ink-400'
    }`;

  return (
    <nav className="lg:hidden fixed bottom-4 left-4 right-4 z-50" ref={menuRef}>
      {/* More menu popup */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMoreOpen(false)}
          />
          <div className="absolute bottom-full mb-2 left-0 right-0 z-50 glass-strong rounded-2xl shadow-warm-lg border border-surface-200/60 py-2 px-2 animate-slide-up">
            <div className="grid grid-cols-3 gap-1">
              {moreItems.map(item =>
                item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMoreOpen(false)}
                    className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl text-xs font-medium transition-all duration-200 text-ink-500 hover:bg-surface-100"
                  >
                    <item.icon className="text-lg" />
                    {item.mobileLabel || item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-1 px-2 py-3 rounded-xl text-xs font-medium transition-all duration-200 ${
                        isActive
                          ? 'text-canopy bg-accent-50'
                          : 'text-ink-500 hover:bg-surface-100'
                      }`
                    }
                  >
                    <item.icon className="text-lg" />
                    {item.mobileLabel || item.label}
                  </NavLink>
                )
              )}
              <a
                href={feedbackHref}
                onClick={() => setMoreOpen(false)}
                className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl text-xs font-medium transition-all duration-200 text-ink-500 hover:bg-surface-100"
              >
                <HiChatAlt className="text-lg" />
                Feedback
              </a>
            </div>
          </div>
        </>
      )}

      {/* Bottom tab bar */}
      <div className="glass-strong rounded-2xl shadow-warm-lg border border-surface-200/60">
        <div className="flex items-center py-2 px-1">
          {primaryItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) => tabClass(isActive)}
            >
              <item.icon className="text-xl" />
              {item.mobileLabel || item.label}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(prev => !prev)}
            className={tabClass(isMoreActive || moreOpen)}
          >
            <HiDotsHorizontal className="text-xl" />
            More
          </button>
        </div>
      </div>
    </nav>
  );
};

export default MobileNav;
